import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "../../data/content";
import { MaskedLineInView } from "../MaskedLine";

const CARD_GROUNDS = ["ground-paper", "ground-highlighter", "ground-lab", "ground-paper", "ground-margin", "ground-paper", "ground-lab", "ground-margin"];

export default function Testimonials() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", slidesToScroll: 1 });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
  }, [embla, onSelect]);

  useEffect(() => {
    if (!embla) return;
    const id = setInterval(() => embla && embla.scrollNext(), 6000);
    return () => clearInterval(id);
  }, [embla]);

  return (
    <section data-testid="testimonials-section" className="ground-margin sec-pad">
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-8 mb-14">
          <div>
            <span className="series-stamp"><span>Marginalia</span></span>
            <h2 className="h-section mt-8">
              <MaskedLineInView>Hear it from</MaskedLineInView>
              <MaskedLineInView delay={0.08}><span className="claim">the room.</span></MaskedLineInView>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => embla && embla.scrollPrev()}
              data-testid="testimonial-prev"
              aria-label="Previous testimonial"
              className="w-12 h-12 border-[1.5px] border-[color:var(--ink)] text-[#0E1330] hover:bg-[#0E1330] hover:text-[color:var(--paper)] flex items-center justify-center transition-all"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => embla && embla.scrollNext()}
              data-testid="testimonial-next"
              aria-label="Next testimonial"
              className="w-12 h-12 border-[1.5px] border-[color:var(--ink)] text-[#0E1330] hover:bg-[#0E1330] hover:text-[color:var(--paper)] flex items-center justify-center transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef} data-testid="testimonials-carousel">
          <div className="flex">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3 px-3">
                <article
                  data-testid={`testimonial-${i}`}
                  className={`${CARD_GROUNDS[i % CARD_GROUNDS.length]} h-full p-8 border-[1.5px] border-[color:var(--ink)]`}
                  style={{ minHeight: 340 }}
                >
                  <span className="stamp" style={{ fontSize: 10.5 }}>★ ★ ★ ★ ★  · verified</span>
                  <p className="font-aside mt-6 text-[19px] leading-[1.5]">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="mt-8 pt-5 rule-strong">
                    <p className="font-hook text-[18px]" style={{ letterSpacing: "-0.015em" }}>{t.name}</p>
                    <p className="stamp-dim mt-2" style={{ fontSize: 10 }}>{t.title}</p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-10">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              onClick={() => embla && embla.scrollTo(i)}
              data-testid={`testimonial-dot-${i}`}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-[3px] transition-all duration-300 ${
                selected === i ? "bg-[color:var(--ink)] w-10" : "bg-[color:rgba(14,19,48,0.25)] w-5"
              }`}
            />
          ))}
        </div>

        <div className="mt-24 border-t-[1.5px] border-[color:var(--ink)] pt-8 grid md:grid-cols-2 gap-8 items-baseline" data-testid="aggregate-rating">
          <div className="font-hook-black text-[#0E1330]" style={{ fontSize: "clamp(80px, 12vw, 180px)", lineHeight: 0.85 }}>
            4.6<span className="text-[color:var(--pink)]">/5</span>
          </div>
          <div>
            <p className="stamp">★ ★ ★ ★ ★  · Aggregate</p>
            <p className="font-aside mt-4 text-[22px]">
              Based on 90,000+ trained professionals across a decade of rooms.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
